import {
  BUILD_MANIFEST_PATH,
  isComparableBuild,
  type BuildInfo,
} from "@/lib/build-info";

/**
 * How long to wait for the manifest before treating the check as failed.
 *
 * A jobsite captive portal or a half-open connection can stall a request for
 * minutes while `navigator.onLine` still reports true. Without a bound the
 * caller's busy state would never clear and the chip would sit on "Checking…",
 * hiding the very build label it exists to show.
 */
const MANIFEST_TIMEOUT_MS = 5 * 1000;

/**
 * Fetches the build manifest published alongside the current deployment.
 *
 * Two cache layers have to be defeated for this to mean anything:
 *
 * 1. The HTTP cache — handled by `cache: "no-store"`.
 * 2. The service-worker precache — handled at build time, where `version.json`
 *    is listed in `globIgnores`. Without that exclusion workbox would precache
 *    the manifest and hand back the copy from the build the tab started on, so
 *    a stale tab would report itself current forever.
 *
 * Resolves `null` on any failure (offline, transient error, malformed body).
 * A failed freshness check is not an error worth surfacing — the caller keeps
 * its last known state and the next trigger retries.
 */
export async function fetchPublishedBuild(
  signal?: AbortSignal,
): Promise<BuildInfo | null> {
  try {
    const response = await fetch(BUILD_MANIFEST_PATH, {
      cache: "no-store",
      signal: signal ?? AbortSignal.timeout(MANIFEST_TIMEOUT_MS),
    });
    if (!response.ok) return null;
    return (await response.json()) as BuildInfo;
  } catch {
    return null;
  }
}

/**
 * Whether the running build has been superseded by the published one.
 *
 * Compares commit SHAs, the only field guaranteed to differ between two
 * genuinely different builds. Returns `false` whenever either side lacks an
 * identity — a local build has nothing to compare, and an unreadable manifest
 * must never be reported as "you are stale".
 */
export function isStale(
  running: BuildInfo,
  published: BuildInfo | null,
): boolean {
  if (!isComparableBuild(running) || !published?.sha) return false;
  return published.sha !== running.sha;
}
